import React, { useEffect, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { geocodeExtenderPopAddressAPI } from "./requests/request";
import { apiErrorMessage } from "./extenderPopCarrierAccessData";
import type { SiteLocation } from "./cpeSite";
import {
  applyGeocodeResult,
  canGeocode,
  draftFromLocation,
  shapeGeocodeResults,
  validateSiteLocation,
  type GeocodeResult,
  type SiteLocationDraft,
} from "./siteLocationForm";

interface GpsFinderModalProps {
  onClose: () => void;
  /** Saves the picked point; rejects with the API error to show. */
  onSave: (point: { latitude: number | null; longitude: number | null }) => Promise<void>;
  tenantId: number;
  location: SiteLocation;
}

const MANUAL_HINT = "Type the latitude and longitude by hand.";

/** Nominatim's usage policy asks for this wherever its results show. */
const OsmAttribution: React.FC = () => (
  <a
    href="https://www.openstreetmap.org/copyright"
    target="_blank"
    rel="noopener noreferrer"
    className="text-xs text-semantic-text-secondary hover:underline"
  >
    Search by © OpenStreetMap contributors
  </a>
);

/**
 * Find the GPS point of a CPE site from its address: looks the address up on
 * OpenStreetMap as it opens and fills the coordinates from the match the user
 * picks. They can always be typed by hand instead. The location name and the
 * address are edited in place, on their own rows.
 */
export default function GpsFinderModal({
  onClose,
  onSave,
  tenantId,
  location,
}: GpsFinderModalProps) {
  const api = useAxiosAuth0();
  const address = location.address ?? "";
  const [draft, setDraft] = useState<SiteLocationDraft>(() =>
    draftFromLocation(location)
  );
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [isLooking, setIsLooking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { body, errors } = validateSiteLocation(draft);
  const set = (field: keyof SiteLocationDraft) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const findGps = async () => {
    setIsLooking(true);
    setLookupMessage(null);
    setResults([]);
    try {
      const response = await geocodeExtenderPopAddressAPI(api, address.trim(), tenantId);
      const matches = shapeGeocodeResults(response.data?.data);
      setResults(matches);
      if (matches.length === 0) {
        setLookupMessage(`No match for this address. ${MANUAL_HINT}`);
      }
    } catch (lookupError) {
      // 502: the lookup service is down. Manual entry stays available.
      setLookupMessage(
        `${apiErrorMessage(lookupError, "The address lookup is unavailable.")} ${MANUAL_HINT}`
      );
    } finally {
      setIsLooking(false);
    }
  };

  // The address is what the modal is for: look it up right away, once.
  useEffect(() => {
    if (canGeocode(address)) findGps();
    else setLookupMessage(`Add the address first to look it up. ${MANUAL_HINT}`);
  }, []);

  const save = async () => {
    if (!body) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave({ latitude: body.latitude ?? null, longitude: body.longitude ?? null });
    } catch (saveError) {
      setError(apiErrorMessage(saveError, "Could not save the GPS"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      isOpen
      onClose={onClose}
      title="Find GPS"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <GenericButton variant="secondary" label="Cancel" onClick={onClose} />
          <GenericButton
            label="Save"
            onClick={save}
            loading={isSaving}
            disabled={!body || isSaving}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-semantic-text-primary min-w-0 break-words">
            {address || "No address yet"}
          </p>
          <GenericButton
            variant="secondary"
            label="Search again"
            onClick={findGps}
            loading={isLooking}
            disabled={isLooking || !canGeocode(address)}
          />
        </div>

        {results.length > 0 && (
          <div className="flex flex-col gap-1">
            <SearchSelect
              key={results.map((r) => r.displayName).join("|")}
              label="Matches"
              placeholder="Pick the right match"
              options={results.map((result, index) => ({
                value: String(index),
                label: result.displayName,
              }))}
              onSelect={(value) => {
                const picked = results[Number(value)];
                if (picked) setDraft((current) => applyGeocodeResult(current, picked));
              }}
            />
            <OsmAttribution />
          </div>
        )}
        {lookupMessage && (
          <div className="flex flex-col gap-1">
            <p className="text-sm text-semantic-text-secondary">{lookupMessage}</p>
            {canGeocode(address) && <OsmAttribution />}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <GenericInput
            label="Latitude"
            placeholder="19.432608"
            value={draft.latitude}
            onChange={set("latitude")}
            hasError={Boolean(errors.latitude)}
            helperText={errors.latitude}
            fullWidth
          />
          <GenericInput
            label="Longitude"
            placeholder="-99.133209"
            value={draft.longitude}
            onChange={set("longitude")}
            hasError={Boolean(errors.longitude)}
            helperText={errors.longitude}
            fullWidth
          />
        </div>

        {error && <p className="text-sm text-semantic-text-error">{error}</p>}
      </div>
    </GenericModal>
  );
}
